import { useTranslation } from '@hopin-team/provider-translation';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { getTicks, scaleBand, scaleLinear } from '@visx/scale';
import { Circle } from '@visx/shape';
import { defaultStyles, TooltipWithBounds, useTooltip } from '@visx/tooltip';
import React, { useState } from 'react';

import {
  DotChartData,
  formatAxisLabel,
  formatTooltipLabel,
  getIntervalUnit,
  getMaxTickWidth,
  getUpperBound,
  numberFormatter,
} from './helpers';

type Props = {
  chartData: DotChartData;
  tooltipTitle?: string;
};

export const DotChart = ({ chartData, tooltipTitle }: Props) => {
  const { t } = useTranslation();
  const unit = getIntervalUnit(chartData);
  const [keyboardIndex, setKeyboardIndex] = useState(0);
  const currentIndex = Math.min(
    keyboardIndex,
    Math.max(0, chartData.length - 1),
  );
  const chartLabel = `${tooltipTitle || t('analytics.chart')} ${t(
    'analytics.over-time',
  )}`;

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<{ name: string; value: number }>();

  return (
    <ParentSize>
      {(parent: { height: number; width: number }) => {
        // Y axis config
        const maxYTicks = 4;
        const paddingBottom = 35;
        const yMax = parent.height && parent.height - paddingBottom;
        const maxValue = Math.max(
          ...chartData.map(data => parseFloat(data[2])),
        );

        const upperYBound = getUpperBound(maxValue, maxYTicks) || 1;
        const numYTicks = Math.min(upperYBound, maxYTicks);
        const yScale = scaleLinear({
          range: [yMax, 0],
          domain: [0, upperYBound],
        });
        const yTicks = getTicks(yScale, numYTicks);

        // X axis config
        const paddingLeft = getMaxTickWidth(yTicks);
        const xMax = parent.width - paddingLeft;

        const xScale = scaleBand({
          range: [0, xMax],
          domain: chartData.map(data => data[0]),
          padding: 0.4,
        });
        const numXTicks = 6;
        const xTicks = getTicks(xScale, numXTicks);

        const showKeyboardPoint = (index: number) => {
          const point = chartData[index];
          if (!point) return;
          const dotX =
            (xScale(point[0]) || 0) + paddingLeft + xScale.bandwidth() / 2;
          const dotY = (yScale(parseFloat(point[2])) ?? 0) - 10;
          setKeyboardIndex(index);
          showTooltip({
            tooltipData: { name: point[3], value: index },
            tooltipTop: dotY,
            tooltipLeft: dotX,
          });
        };

        const handleKeyDown = (
          event: React.KeyboardEvent<HTMLInputElement>,
        ) => {
          if (event.key === 'Escape') {
            if (tooltipOpen) {
              event.preventDefault();
              event.stopPropagation();
              hideTooltip();
            }
            return;
          }

          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight' || event.key === 'ArrowUp')
            nextIndex++;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown')
            nextIndex--;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = chartData.length - 1;
          else return;

          event.preventDefault();
          showKeyboardPoint(
            Math.max(0, Math.min(nextIndex, chartData.length - 1)),
          );
        };

        return (
          <div
            data-testid="dot-chart"
            className="relative h-full w-full focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-blue-500"
          >
            <input
              type="range"
              className="sr-only"
              min={0}
              max={chartData.length - 1}
              value={currentIndex}
              aria-label={chartLabel}
              aria-valuetext={
                chartData[currentIndex]
                  ? `${chartData[currentIndex][3]}, ${formatTooltipLabel(
                      chartData[currentIndex][0],
                      unit,
                    )} ${t('analytics.to')} ${formatTooltipLabel(
                      chartData[currentIndex][1],
                      unit,
                    )}, ${tooltipTitle}: ${chartData[currentIndex][2]}, ${t(
                      'analytics.registrants',
                    )}: ${chartData[currentIndex][4]}, ${t(
                      'analytics.attendees',
                    )}: ${chartData[currentIndex][5]}`
                  : undefined
              }
              onFocus={() => showKeyboardPoint(currentIndex)}
              onBlur={hideTooltip}
              onKeyDown={handleKeyDown}
              onChange={event =>
                showKeyboardPoint(Number(event.currentTarget.value))
              }
            />
            <svg aria-hidden="true" className="h-full w-full overflow-visible">
              <Group>
                {chartData.map((data, index) => {
                  const xVal = data[0];
                  const yVal = parseFloat(data[2]);
                  const dotWidth = xScale.bandwidth();
                  const dotHeight = yMax && yMax - (yScale(yVal) ?? 0);
                  const dotX = (xScale(xVal) || 0) + paddingLeft + dotWidth / 2;
                  const dotY = yMax - dotHeight;

                  return (
                    <g key={index}>
                      {/* {dotX !== dotX2 && (
                        <rect
                          x={dotX - 6}
                          y={dotY - 6}
                          width={dotX2 - dotX}
                          height={12}
                          rx={6}
                          className="fill-blue-500 opacity-10"
                          onMouseOver={e =>
                            showTooltip({
                              tooltipData: { name: xVal, value: yVal },
                              tooltipTop: localPoint(e)?.y,
                              tooltipLeft: localPoint(e)?.x,
                            })
                          }
                          onMouseLeave={hideTooltip}
                        />
                      )} */}
                      <Circle
                        cx={dotX}
                        cy={dotY - 10}
                        r={6}
                        className="fill-blue-500"
                        onMouseOver={e =>
                          showTooltip({
                            tooltipData: {
                              name: data[3],
                              value: index,
                            },
                            tooltipTop: localPoint(e)?.y,
                            tooltipLeft: localPoint(e)?.x,
                          })
                        }
                        onMouseLeave={hideTooltip}
                      />
                    </g>
                  );
                })}
              </Group>
              <AxisBottom
                scale={xScale}
                top={yMax}
                left={paddingLeft}
                numTicks={numXTicks}
                tickValues={xTicks}
                hideTicks
                strokeWidth={2}
                axisLineClassName="stroke-gray-300"
                tickLabelProps={tick => ({
                  textAnchor: tick === xTicks[0] ? 'start' : 'middle',
                })}
                tickComponent={props => {
                  const formattedAxisLabel = formatAxisLabel(
                    xTicks,
                    props.formattedValue,
                    unit,
                  );

                  return (
                    <svg textAnchor={props.textAnchor}>
                      <text
                        x={props.x}
                        y={props.y}
                        className="font-inter fill-gray-650 text-xs"
                      >
                        <tspan>{formattedAxisLabel.text}</tspan>
                        <tspan x={props.x} dy="1.2em">
                          {formattedAxisLabel.subText}
                        </tspan>
                      </text>
                    </svg>
                  );
                }}
              />
              <AxisLeft
                scale={yScale}
                left={paddingLeft}
                numTicks={numYTicks}
                axisClassName="fill-gray-650"
                axisLineClassName="stroke-gray-300"
                tickLength={4}
                hideTicks
                strokeWidth={2}
                tickFormat={numberFormatter}
                tickLabelProps={() => ({
                  fontSize: 12,
                  textAnchor: 'end',
                  dy: '-0.2em',
                  dx: '-.33em',
                })}
                labelClassName="font-inter fill-gray-650"
                labelOffset={10}
              />
            </svg>
            <table className="sr-only">
              <caption>{chartLabel}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('analytics.event')}</th>
                  <th scope="col">{t('analytics.date-range')}</th>
                  <th scope="col">{tooltipTitle}</th>
                  <th scope="col">{t('analytics.registrants')}</th>
                  <th scope="col">{t('analytics.attendees')}</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((point, index) => (
                  <tr key={`${point[0]}-${index}`}>
                    <td>{point[3]}</td>
                    <td>
                      {formatTooltipLabel(point[0], unit)} {t('analytics.to')}{' '}
                      {formatTooltipLabel(point[1], unit)}
                    </td>
                    <td>{point[2]}</td>
                    <td>{point[4]}</td>
                    <td>{point[5]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tooltipOpen && tooltipData && (
              <TooltipWithBounds
                key={Math.random()} // Set to random so it correctly updates with parent bounds
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  backgroundColor: '#05141f',
                  opacity: 0.7,
                  padding: 16,
                  borderRadius: 8,
                  minWidth: 200,
                  maxWidth: 500,
                }}
              >
                <div className="w-full">
                  <p className="font-inter text-sm font-bold text-white mb-1">
                    {tooltipData.name}
                  </p>
                  <p className="font-inter mb-4 text-sm text-white">
                    {formatTooltipLabel(chartData[tooltipData.value][0], unit)}
                    {' > '}
                    {formatTooltipLabel(chartData[tooltipData.value][1], unit)}
                  </p>
                  <div className="font-inter text-sm text-white flex justify-between gap-4 mb-2">
                    {tooltipTitle}
                    <div className="font-inter text-sm font-bold text-white">
                      {chartData[tooltipData.value][2]}
                    </div>
                  </div>
                  <div className="font-inter text-sm text-white flex justify-between mb-2">
                    {t('analytics.registrants')}
                    <div className="font-inter text-sm font-bold text-white">
                      {chartData[tooltipData.value][4]}
                    </div>
                  </div>
                  <div className="font-inter text-sm text-white flex justify-between">
                    {t('analytics.attendees')}
                    <div className="font-inter text-sm font-bold text-white">
                      {chartData[tooltipData.value][5]}
                    </div>
                  </div>
                </div>
              </TooltipWithBounds>
            )}
          </div>
        );
      }}
    </ParentSize>
  );
};
